import { c as _c } from "react/compiler-runtime";
import { translateInline } from '@/globals/translate/translateInline';
import { h3TextBlock } from '@/uiComposites/typography/headings';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { stack } from '@/uiPrimitives/layout/stack';
import { unprintable } from '@/uiPrimitives/base/printable.css';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { QuestionTranslation, questionTranslations } from './legalForm.config';
import { useGivenAnswer, useGivenAnswers } from './legalFormHooks';
import { useDOPRouter } from '@/globals/routing/DOPRouter';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { useIsPlural } from './CurrentQuestion';
import { useQuestionIndexContext } from './useQuestionIndex';
import { addTestSelector } from '@/globals/testHelpers/testSelector';
import { convertGivenAnswersToSearchParams } from './LegalFormAnalyticsProvider';
import { pushPreviousStepEvent, pushRestartEvent } from '@/globals/analytics/events/pushDecisionToolEvent';

/**
 * Component to display the questions that have been answered by the user.
 * It allows users to see their answers and edit them if necessary.
 */
export const AnsweredQuestion = t0 => {
  const $ = _c(33);
  const {
    question,
    index
  } = t0;
  const [, setQuestionIndex] = useQuestionIndexContext();
  const [givenAnswer] = useGivenAnswer(question.id);
  let t1;
  if ($[0] !== givenAnswer || $[1] !== question.answers) {
    let t2;
    if ($[3] !== givenAnswer) {
      t2 = answer => answer.id === givenAnswer;
      $[3] = givenAnswer;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = question.answers.find(t2);
    $[0] = givenAnswer;
    $[1] = question.answers;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selectedAnswer = t1;
  const isPlural = useIsPlural();
  let t2;
  if ($[5] !== index || $[6] !== isPlural || $[7] !== selectedAnswer) {
    t2 = isPlural && selectedAnswer?.text.plural ? selectedAnswer.text.plural : selectedAnswer?.text.single || `${questionTranslations[index].shortTitle}: ${translateInline({
      en: "no answer selected",
      nl: "geen antwoord geselecteerd"
    })}`;
    $[5] = index;
    $[6] = isPlural;
    $[7] = selectedAnswer;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const answerText = t2;
  let t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = classes(shelf({
      gap: "-1 | small",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      alignItems: "baseline"
    }));
    t4 = classes(textBlock({
      capLineHeight: "tight"
    }));
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  const t5 = `uiAnsweredQuestion-${question.id}`;
  let t6;
  if ($[11] !== t5) {
    t6 = addTestSelector(t5);
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== answerText || $[14] !== t6) {
    t7 = <span className={t4} {...t6}>{answerText}</span>;
    $[13] = answerText;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== index || $[17] !== setQuestionIndex) {
    t8 = () => {
      pushPreviousStepEvent({
        questionId: "-",
        questionTitle: "-"
      });
      setQuestionIndex({
        index,
        cause: "edit"
      });
    };
    $[16] = index;
    $[17] = setQuestionIndex;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = classes(unprintable, textBlock({
      color: "positiveBlue",
      textDecorationLine: "underline"
    }));
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = `Edit question ${question.title}`;
  const t11 = `Wijzig vraag ${question.title}`;
  let t12;
  if ($[20] !== t10 || $[21] !== t11) {
    t12 = translateInline({
      en: t10,
      nl: t11
    });
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = `uiChangeAnswer-${question.id}`;
  let t14;
  if ($[23] !== t13) {
    t14 = addTestSelector(t13);
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = translateInline({
      en: "Edit",
      nl: "Wijzigen"
    });
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t12 || $[27] !== t14 || $[28] !== t8) {
    t16 = <button onClick={t8} type="button" className={t9} aria-label={t12} {...t14}>{t15}</button>;
    $[26] = t12;
    $[27] = t14;
    $[28] = t8;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t16 || $[31] !== t7) {
    t17 = <div className={t3}>{t7}{t16}</div>;
    $[30] = t16;
    $[31] = t7;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  return t17;
};
export const AnsweredQuestions = () => {
  const $ = _c(12);
  const router = useDOPRouter();
  const givenAnswers = useGivenAnswers();
  const [, setQuestionIndex] = useQuestionIndexContext();
  let t0;
  if ($[0] !== givenAnswers || $[1] !== router || $[2] !== setQuestionIndex) {
    t0 = () => {
      pushRestartEvent({
        givenAnswersSearchParamsString: convertGivenAnswersToSearchParams(givenAnswers).toString()
      });
      setQuestionIndex({
        index: 0,
        cause: "reset"
      });
      router.replaceClient("?");
    };
    $[0] = givenAnswers;
    $[1] = router;
    $[2] = setQuestionIndex;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleReset = t0;
  let t1;
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = classes(box({
      backgroundColor: "negativeBlue",
      paddingInline: "2 | h2",
      paddingBlockStart: "3 | h1"
    }), stack({
      rowGap: "7 | betweenH3Sections"
    }));
    t2 = <h2 className={classes(h3TextBlock({}))}>{translateInline({
        en: "Your answers",
        nl: "Uw antwoorden"
      })}</h2>;
    t3 = <div className={classes(stack({
      rowGap: "4 | betweenListItems"
    }))}>{questionTranslations.map(_temp)}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = classes(unprintable, box({
      inlineSize: "fit-content",
      paddingBlock: "3 | h1",
      paddingInline: indentSize,
      backgroundColor: "negative",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "positiveBlue"
    }), textStyle({
      fontWeight: "bold",
      capLineHeight: "tight",
      textDecorationLine: "none",
      color: "positiveBlue:noVisited",
      textAlign: "center"
    }));
    t5 = addTestSelector("uiRestartButton");
    t6 = <span className={classes(textBlock({}))}>{translateInline({
        en: "Restart",
        nl: "Start opnieuw"
      })}</span>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[10] !== handleReset) {
    t7 = <div className={t1}>{t2}{t3}<button onClick={handleReset} type="button" className={t4} {...t5}>{t6}</button></div>;
    $[10] = handleReset;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
};
function _temp(question, index) {
  return <AnsweredQuestion key={question.id} question={question} index={index} />;
}